import { useCallback, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { DEFAULT_LANGUAGE } from 'constants/languages';
import {
  CreationAudioInfo,
  CreationVideoInfo,
} from 'redux/middleware/api/creation-service/types';
import { directUpload } from 'redux/middleware/api/media-upload-service/actions';
import { waitForEddyProject } from 'redux/modules/eddy/actions';
import {
  eddyProjectCreationProgressPercentageSelector,
  eddyProjectCuidSelector,
  eddyProjectStatusSelector,
  isWaitingForEddyProjectSelector,
} from 'redux/modules/eddy/selectors';
import { showError } from 'redux/modules/notification';
import { uploadVideoToStorage } from 'redux/modules/video-upload/actions';
import {
  clearCreateCreationAsset,
  createCreationAsset,
} from 'redux/modules/wizard-export/actions';
import { Dispatch } from 'redux/types';
import { ProjectCreationMethod } from 'types/common';
import { DEFAULT_PROJECT_NAME } from 'utils/constants';
import { preSignedUpload } from 'utils/request';
import { VideoTemplateEditorMedia } from '../context';
import { useEditorState } from '../context/VideoEditorStateContext';
import { useTemplateState } from '../context/VideoTemplateStateContext';

type MediaInfo = string | CreationAudioInfo | CreationVideoInfo | undefined;

export type UseHandleTranscribeResult = {
  media: VideoTemplateEditorMedia;
  isLoadingMediaInfo: boolean;
  isTranscribing: boolean;
  transcribingProgress: number;
  eddyProjectCuid: string;
  startTranscribeProcess: () => void;
  stopTranscribeProcess: () => void;
};

export default function useHandleTranscribe(): UseHandleTranscribeResult {
  const [mediaInfo, setMediaInfo] = useState<MediaInfo>(undefined);
  const [isLoadingMediaInfo, setIsLoadingMediaInfo] = useState(false);

  const isTranscribingCancelledRef = useRef(false);

  const isTranscribing = useSelector(isWaitingForEddyProjectSelector);
  const transcribingProgress = useSelector(
    eddyProjectCreationProgressPercentageSelector,
  );
  const eddyProjectCuid = useSelector(eddyProjectCuidSelector);
  const eddyProjectStatus = useSelector(eddyProjectStatusSelector);

  const {
    media,
    eddyProjectVisibility,
    projectName = DEFAULT_PROJECT_NAME,
    podcastIdentifier,
    defaultEmbedConfig,
  } = useEditorState();
  const { transcription, soundwave } = useTemplateState();

  const { source, type, mediaImportedArgs } = media ?? {};
  const isAudio = type === 'audio';

  const dispatch = useDispatch<Dispatch>();

  const handleAudioUpload = useCallback(async (): Promise<
    CreationAudioInfo | undefined
  > => {
    if (typeof source === 'string') {
      return undefined;
    }

    const { response } = await dispatch(directUpload(source));

    await preSignedUpload(response.upload.url, source, response.upload.fields);

    return {
      audioDirectUploadBucket: response?.bucket,
      audioDirectUploadKey: response?.key,
    };
  }, [dispatch, source]);

  const handleVideoUpload = useCallback(async (): Promise<
    CreationVideoInfo | undefined
  > => {
    const videoUploadResult = await dispatch(
      uploadVideoToStorage(
        {
          src: source,
          language: DEFAULT_LANGUAGE,
        },
        mediaImportedArgs,
        { waitForUpload: true },
      ),
    );

    return {
      uploadedVideoId: videoUploadResult?.get('id'),
      isVideoTranscriptEnabled: true,
    };
  }, [dispatch, mediaImportedArgs, source]);

  const uploadMediaSource = useCallback(async (): Promise<MediaInfo> => {
    if (isTranscribingCancelledRef.current) {
      return undefined;
    }

    try {
      setIsLoadingMediaInfo(true);

      const result = isAudio
        ? await handleAudioUpload()
        : await handleVideoUpload();

      return result;
    } catch {
      dispatch(
        showError({
          message: 'Error uploading media, please try again',
        }),
      );

      return undefined;
    } finally {
      setIsLoadingMediaInfo(false);
    }
  }, [dispatch, handleAudioUpload, handleVideoUpload, isAudio]);

  const resolveMediaInfo = useCallback(async (): Promise<MediaInfo> => {
    if (mediaInfo) {
      return mediaInfo;
    }

    if (typeof source === 'string' && isAudio) {
      setMediaInfo(source);

      return source;
    }

    const uploadedMediaSource = await uploadMediaSource();

    setMediaInfo(uploadedMediaSource);

    return uploadedMediaSource;
  }, [isAudio, mediaInfo, source, uploadMediaSource]);

  const startTranscribeProcess = useCallback(async (): Promise<void> => {
    isTranscribingCancelledRef.current = false;

    if (
      eddyProjectStatus === 'completed' ||
      defaultEmbedConfig?.captions?.captionSource?.eddyProjectCuid
    ) {
      return;
    }

    const recordingId =
      defaultEmbedConfig?.recordingId ||
      defaultEmbedConfig?.audioInfo?.[0]?.recordingId;

    const resolvedMediaInfo = await resolveMediaInfo();

    if (eddyProjectCuid) {
      dispatch(waitForEddyProject(eddyProjectCuid));

      return;
    }

    if (isTranscribingCancelledRef.current) {
      return;
    }

    try {
      const basePayload = {
        audioLanguage: transcription?.language ?? DEFAULT_LANGUAGE,
        projectName,
        projectCreateMethod: (isAudio
          ? 'episode'
          : 'videoTranscriptEpisode') as ProjectCreationMethod,
        eddyProjectVisibility,
        podcastIdentifier,
        soundwave,
      };

      let mediaPayload =
        typeof resolvedMediaInfo === 'string'
          ? { resolvedMediaInfo }
          : resolvedMediaInfo;

      if (isAudio) {
        mediaPayload = {
          ...mediaPayload,
          audioUrl:
            typeof resolvedMediaInfo === 'string' ? resolvedMediaInfo : null,
          recordingId,
        };
      }

      const result = await dispatch(
        createCreationAsset({
          ...mediaPayload,
          ...basePayload,
        }),
      );

      if (isTranscribingCancelledRef.current) {
        return;
      }

      dispatch(waitForEddyProject(result.captionSourceEddyProjectCuid));
    } catch {
      dispatch(
        showError({
          message: 'Something went wrong, please try again',
        }),
      );
    }
  }, [
    defaultEmbedConfig?.audioInfo,
    defaultEmbedConfig?.captions?.captionSource?.eddyProjectCuid,
    defaultEmbedConfig?.recordingId,
    dispatch,
    eddyProjectCuid,
    eddyProjectStatus,
    eddyProjectVisibility,
    isAudio,
    podcastIdentifier,
    projectName,
    resolveMediaInfo,
    soundwave,
    transcription?.language,
  ]);

  const stopTranscribeProcess = useCallback((): void => {
    if (eddyProjectStatus === 'completed') {
      return;
    }

    isTranscribingCancelledRef.current = true;

    dispatch(clearCreateCreationAsset());
    setMediaInfo(undefined);
    setIsLoadingMediaInfo(false);
  }, [dispatch, eddyProjectStatus]);

  return {
    media,
    isLoadingMediaInfo,
    isTranscribing,
    transcribingProgress,
    eddyProjectCuid:
      eddyProjectCuid ??
      defaultEmbedConfig?.captions?.captionSource?.eddyProjectCuid,
    startTranscribeProcess,
    stopTranscribeProcess,
  };
}
